import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PaneResizeProps } from '../../hooks/useSidePane';
import Tooltip from '../ui/Tooltip';

/**
 * The two controls every workstation side pane needs, shared so the left explorer and the right chat pane
 * behave identically (RF3-WORKSTATION slice E). Both panes fold to the same 3rem strip and both toggles sit
 * on the pane's CENTRE-FACING edge — the control always points at the canvas it yields space to.
 *
 * Both name themselves on hover (owner law 2026-08-24: *"need hover-over tooltip whenever we have controls,
 * icons"*) through the app's own `Tooltip` rather than the native `title=`, which is too slow to read as an
 * answer and invisible on touch.
 */

interface ToggleProps {
  /** Which pane this belongs to — decides the icon direction and the wording. */
  side: 'left' | 'right';
  onCollapse: () => void;
  className?: string;
}

/**
 * ONE ROUND CHEVRON, ON THE BORDER — the same control in both directions.
 *
 * Owner 2026-08-24 (screenshots 98a/98b): *"these icons better to be side border icons? it doesn't make sense
 * that an entire row is used to fit them"*, then indexalpha.ai's research sidebar as the reference: a small
 * circular chevron riding the pane's edge near the top, and a folded pane that still SAYS WHAT IT IS.
 *
 * So collapse and expand are now the same button pointing opposite ways, in the same place, at the same size.
 * A pane that folds and one that unfolds should not feel like two different mechanisms.
 *
 * It stays ALWAYS VISIBLE rather than appearing on hover: three separate defects this month came from a pane
 * control that was not on screen when it was needed, and hover would be re-earning that bug for a few pixels.
 *
 * The pane it sits in must be `relative`.
 */
const CHEVRON = `flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-secondary-200
                 bg-white text-secondary-400 transition-colors hover:text-secondary-800
                 dark:border-secondary-600 dark:bg-secondary-900 dark:hover:text-secondary-100`;

/**
 * The fold control itself. In-flow, not absolutely positioned (owner 2026-08-24: *"now the icons can have
 * its row to avoid overlapping"*) — riding over the pane's first row saved space but sat on top of whatever
 * was under it. `PaneToggleRow` gives it a home that costs 32px rather than the ~40px header row it started
 * as, so the space argument is answered without the overlap.
 */
export const PaneToggle: React.FC<ToggleProps> = ({ side, onCollapse, className = '' }) => {
  const label = side === 'left' ? 'Collapse the explorer' : 'Collapse the chat pane';
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <Tooltip content={label} side="bottom">
      <button type="button" onClick={onCollapse} aria-label={label} aria-expanded className={`${CHEVRON} ${className}`}>
        <Icon className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
};

/**
 * The slim row the fold control lives in, pushed to the pane's INNER edge — the side facing the canvas it
 * yields space to, matching where the chevron sits on the folded strip so the control does not appear to
 * jump when a pane opens.
 */
export const PaneToggleRow: React.FC<{ side: 'left' | 'right'; onCollapse: () => void }> = ({ side, onCollapse }) => (
  <div
    data-testid={`pane-toggle-row-${side}`}
    className={`flex h-8 shrink-0 items-center px-1.5 ${side === 'left' ? 'justify-end' : 'justify-start'}`}
  >
    <PaneToggle side={side} onCollapse={onCollapse} />
  </div>
);

interface RailProps {
  side: 'left' | 'right';
  onExpand: () => void;
  /**
   * The one signal that must survive the fold — an in-flight render on the left, unread crew replies on the
   * right. A 20–40 minute render or a reply from the crew must never become invisible just because the pane
   * is closed (screenshot79's rule, and the owner's reason for wanting the right pane collapsible at all).
   */
  children?: React.ReactNode;
}

/**
 * THE FOLDED PANE — a slim strip that still says what it is.
 *
 * Owner 2026-08-24: folded, it was *"not showing anything"*, and an empty gutter reads as a broken layout
 * rather than as a closed drawer. The reference (indexalpha.ai's research sidebar) keeps the pane's NAME set
 * vertically down the strip with the expand chevron at its top — so the folded state answers both "what is
 * this?" and "how do I get it back?" without being unfolded.
 *
 * `w-8` is the 2rem in SIDE_PANE_RAIL, so the canvas reclaims precisely what the pane gave up.
 */
export const PaneRail: React.FC<RailProps> = ({ side, onExpand, children }) => {
  const label = side === 'left' ? 'Expand the explorer' : 'Expand the chat pane';
  const name = side === 'left' ? 'Your series' : 'Crew';
  const Icon = side === 'left' ? ChevronRight : ChevronLeft;
  return (
    <div
      data-testid={`pane-rail-${side}`}
      className={`hidden h-full w-8 shrink-0 flex-col items-center border-secondary-200 bg-white py-1
                  dark:border-secondary-600 dark:bg-black md:flex ${side === 'left' ? 'border-r' : 'border-l'}`}
    >
      {/* Same 32px row as an OPEN pane's toggle, so the chevron holds its position through the fold. */}
      <div className="flex h-8 shrink-0 items-center">
        <Tooltip content={label} side="bottom">
          <button type="button" onClick={onExpand} aria-label={label} aria-expanded={false} className={CHEVRON}>
            <Icon className="h-3.5 w-3.5" />
          </button>
        </Tooltip>
      </div>
      {/* The pane's name, running down the strip. Reading upward on the left and downward on the right keeps
          each label rotated AWAY from the canvas, so neither reads as if it belongs to the middle. */}
      <span
        className={`mt-1 select-none whitespace-nowrap text-[11px] font-semibold uppercase tracking-widest
                    text-secondary-400 dark:text-secondary-500
                    ${side === 'left' ? '[writing-mode:vertical-rl] rotate-180' : '[writing-mode:vertical-rl]'}`}
      >
        {name}
      </span>
      {/* Whatever signal must survive the fold — an in-flight render, unseen crew replies — below the name. */}
      {children && <span className="mt-3">{children}</span>}
    </div>
  );
};

interface HandleProps extends PaneResizeProps {
  side: 'left' | 'right';
}

/**
 * The pane's inner border, made draggable (owner 2026-08-21: "like the panes in vscode"). It sits INSIDE the
 * pane rather than straddling the border, because the Studio is a fixed overlay that starts exactly where the
 * pane ends — a handle hanging even 4px into the canvas would be underneath it and unclickable.
 *
 * `touch-none` hands the gesture to Pointer Events instead of the browser's scroll/selection handling, and
 * the separator role + arrow keys give the same resize to anyone not using a pointer.
 */
export const PaneResizeHandle: React.FC<HandleProps> = ({ side, dragging, ...handlers }) => (
  <div
    {...handlers}
    role="separator"
    aria-orientation="vertical"
    tabIndex={0}
    data-testid={`pane-resize-${side}`}
    aria-label={side === 'left' ? 'Resize the explorer' : 'Resize the chat pane'}
    title="Drag to resize, double-click to reset"
    className={`absolute inset-y-0 ${side === 'left' ? 'right-0' : 'left-0'} z-10 hidden w-1.5 cursor-col-resize
                touch-none select-none md:block ${dragging ? 'bg-secondary-300 dark:bg-secondary-600' : ''}
                hover:bg-secondary-200 focus-visible:bg-secondary-300 dark:hover:bg-secondary-700`}
  />
);

/**
 * The monochrome unread mark — one dot, used by a contact row and by the folded pane that would otherwise
 * hide that row entirely. Same shape both places so "there is something new" reads identically.
 */
export const UnreadDot: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    className={`shrink-0 h-2 w-2 rounded-full bg-secondary-900 dark:bg-white ${className}`}
    aria-label="New messages"
    title="New messages"
  />
);

/** The wording for the crew mark, shared so the row caption and the mark can never drift apart. */
export const CREW_MARKER_LABEL = 'Crew for the production you’re editing';

/**
 * "These are the people on the production you have open." It sits on the row's LEADING edge as a short BAR —
 * deliberately neither the shape nor the position of `UnreadDot`, because both marks can land on the SAME row
 * and a second solid dot would just read as a second unread signal. Position carries the meaning: left = which
 * crew belongs to your open work, right = there is something new to read.
 *
 * Absolutely positioned so a non-crew row keeps its exact alignment — the mark must not push the list around.
 */
export const CrewMarker: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    data-testid="crew-marker"
    className={`pointer-events-none absolute inset-y-1.5 left-0 w-1 rounded-r-full bg-secondary-900
                dark:bg-white ${className}`}
    role="img"
    aria-label={CREW_MARKER_LABEL}
    title={CREW_MARKER_LABEL}
  />
);
